import { useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { terraProxyApiClient } from '../clients/testerClient';
import { useHarPresenceStore } from '../store/harPresenceStore';

/**
 * Resolves which tool calls in a thread have captured HTTP artifacts (HARs).
 * Presence is seeded from the tracker projection on load and kept live by
 * `useToolHarSSE`, which marks new HARs present as they are stored.
 */
export const useToolHarPresence = (
  threadId: string | undefined,
): { hasHarPresence: (toolCallId: string) => boolean } => {
  const setThreadPresence = useHarPresenceStore(
    (state) => state.setThreadPresence,
  );
  const presentToolCallIds = useHarPresenceStore((state) =>
    threadId ? state.presenceByThread[threadId] : undefined,
  );

  const { data } = useQuery({
    queryKey: ['tool-har-presence', threadId],
    queryFn: async (): Promise<string[]> => {
      if (!threadId) {
        return [];
      }
      const response =
        await terraProxyApiClient.getToolHarPresenceByThread(threadId);
      return response?.toolCallIds ?? [];
    },
    enabled: !!threadId,
    staleTime: 30 * 1000,
  });

  useEffect(() => {
    if (threadId && data) {
      setThreadPresence(threadId, data);
    }
  }, [threadId, data, setThreadPresence]);

  const hasHarPresence = useCallback(
    (toolCallId: string): boolean =>
      presentToolCallIds?.has(toolCallId) ?? false,
    [presentToolCallIds],
  );

  return { hasHarPresence };
};
